// src/app/auth/auth-guard.ts

import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from './service/auth.service';
import { PermissionsStoreService } from './service/permissions/permissions-store.service';
import { from, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';

/**
 * Gate for every /securehome route. Two checks, in order:
 *   1. Authenticated? (valid Cognito/SSO session) — else → /login
 *   2. Activated?     (an admin has flipped the user's {org}-Users record on)
 *                     — else → /pending-activation
 *
 * The activation record is loaded once per session via the permissions store
 * (shared with SecureHomeComponent so it isn't fetched twice). A missing record
 * (e.g. a freshly auto-provisioned first-time user) reads as not-activated and
 * routes to the pending screen. Redirects return a UrlTree so there's no
 * imperative navigate()+false double-trigger. The /pending-activation route is a
 * top-level sibling of /login, NOT a securehome child, so this guard never runs
 * on it (no redirect loop).
 *
 * Every /login redirect carries the requested URL as `returnUrl`, and
 * LoginComponent sends the user there once authenticated. Without it, an
 * emailed deep link (the timecard/timesheet action mail's "review this" button)
 * was silently discarded on a cold click: the guard bounced to /login and login
 * navigated to a hardcoded /securehome/my-work, so the recipient landed on a
 * generic queue and the item they were sent never opened. Mail is nearly always
 * opened without a live session, so that was the common path, not an edge case.
 */
export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const permissionsStore = inject(PermissionsStoreService);
  const router = inject(Router);

  // Send the user back where they were headed after they sign in.
  const loginUrlTree = () =>
    router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });

  return from(authService.isAuthenticated()).pipe(
    switchMap(isAuthenticated => {
      if (!isAuthenticated) {
        return of(loginUrlTree());
      }
      return from(authService.getUserEmail()).pipe(
        switchMap(email => {
          if (!email) {
            return of(loginUrlTree());
          }
          return from(permissionsStore.ensureLoaded(email)).pipe(
            map(() => permissionsStore.isActivated()
              ? true
              : router.createUrlTree(['/pending-activation']))
          );
        })
      );
    }),
    catchError(error => {
      // If there's an error, redirect to login
      console.error('Guard: error during auth/activation check.', error);
      return of(loginUrlTree());
    })
  );
};